/**
 * Session state machine (PRD `docs/prd-platform-evolution.md` §6.8, Appendix A).
 *
 * A pure reducer over plain state, in the style of `lib/attainment.ts` and
 * `lib/focusCapacity.ts`. It owns **time and phase** and nothing else: no API
 * calls, no question text, no chat transcript, no mock answers. Those belong to
 * the components, which hold this state in a `ref` and dispatch events into
 * {@link reduce}.
 *
 * Keeping it a reducer rather than a class of `ref`s is what makes the
 * interesting behaviour — block accounting, the idle grace, the break boundary,
 * goal completion — testable without mounting anything or waiting on a clock.
 *
 * The clock is always injected as an explicit `atMs` on the events that need
 * it. Nothing here reads `Date.now()`.
 */

import { timeOptions } from "./focusCapacity";

/**
 * `idle` is this module's name for the PRD's `phase: null` — a string
 * discriminant beats a nullable one for exhaustive switches.
 *
 * Mock deliberately bypasses `focusing`: it runs bare under exam conditions,
 * with no focus bar, no break offer and no nudge.
 */
export type SessionPhase =
  | "idle"
  | "planning"
  | "booting"
  | "focusing"
  | "break"
  | "complete"
  | "mockRunning"
  | "mockResults";

export type SessionMode = "question" | "tutor" | "mock";

export type GoalType = "time" | "marks";

/** Mirrors `focus_blocks.ended_reason` (contract D1). */
export type EndedReason =
  "goal_met" | "break_taken" | "ended_early" | "abandoned";

export interface SessionScope {
  kind: "topic" | "subject";
  subjectId: string;
  subjectName: string;
  /** Absent for a subject-scoped session until the caller resolves one. */
  topicId?: string;
  topicName?: string;
}

/**
 * A closed focus block, shaped for `POST`/`PATCH /focus-blocks` (contract D2).
 * The reducer accumulates these so the component can flush them; it never
 * performs the request itself.
 */
export interface FocusBlockRecord {
  blockIndex: number;
  startedAtMs: number;
  endedAtMs: number;
  activeSeconds: number;
  idleSecondsTotal: number;
  endedReason: EndedReason;
  goalMet: boolean;
  nudgeShown: number;
  nudgeDismissed: number;
  breakOffered: boolean;
  breakTaken: boolean;
  breakSkipped: boolean;
}

interface OpenBlock {
  blockIndex: number;
  startedAtMs: number;
  activeSeconds: number;
  idleSeconds: number;
  nudgeShown: number;
  nudgeDismissed: number;
  breakOffered: boolean;
  breakTaken: boolean;
  breakSkipped: boolean;
}

export interface FocusSessionState {
  phase: SessionPhase;
  mode: SessionMode;
  scope: SessionScope | null;

  goalType: GoalType;
  /**
   * Index into the capacity-derived options, **never** a minute count. The
   * absolute values move whenever capacity changes, so storing minutes silently
   * desyncs the selection (PRD PE-7.5).
   */
  goalIndex: number;
  goalMarks: number;

  /** Wall-clock seconds since the first block opened, across breaks. */
  elapsedSeconds: number;
  /** Seconds credited as work — excludes sustained idle. See {@link reduce}. */
  activeSeconds: number;
  idleSeconds: number;
  /** Seconds since the last sign of life; drives the nudge. */
  secondsSinceActivity: number;
  nudgeVisible: boolean;

  /** Set once the break boundary is crossed, so the offer is made only once. */
  breakOffered: boolean;
  breakDeclined: boolean;
  breakSecondsLeft: number;

  marksEarned: number;
  questionsAnswered: number;
  exchanges: number;

  mockSecondsLeft: number;
  mockIndex: number;

  openBlock: OpenBlock | null;
  completedBlocks: FocusBlockRecord[];
  /** Reason the session as a whole ended; null until it does. */
  endedReason: EndedReason | null;
}

export interface FocusSessionConfig {
  /** Break is *offered* here, never forced (PRD PE-8.5). */
  blockBoundarySeconds: number;
  /** Sustained inactivity before the nudge, and the idle-credit grace. */
  idleNudgeSeconds: number;
  breakSeconds: number;
  mockSeconds: number;
}

/**
 * Production defaults. The prototype ran a 30x accelerated clock
 * (`TICK = 30`); production ticks in real seconds.
 */
export const DEFAULT_CONFIG: FocusSessionConfig = {
  blockBoundarySeconds: 25 * 60,
  idleNudgeSeconds: 150,
  breakSeconds: 5 * 60,
  mockSeconds: 20 * 60,
};

export const DEFAULT_GOAL_MARKS = 40;
export const MARKS_GOAL_OPTIONS: readonly number[] = [20, 40, 60];

export function initialState(): FocusSessionState {
  return {
    phase: "idle",
    mode: "question",
    scope: null,
    goalType: "time",
    // Defaults to the middle option — "what I can currently hold" — rather than
    // the stretch, so the stretch stays a deliberate choice.
    goalIndex: 1,
    goalMarks: DEFAULT_GOAL_MARKS,
    elapsedSeconds: 0,
    activeSeconds: 0,
    idleSeconds: 0,
    secondsSinceActivity: 0,
    nudgeVisible: false,
    breakOffered: false,
    breakDeclined: false,
    breakSecondsLeft: DEFAULT_CONFIG.breakSeconds,
    marksEarned: 0,
    questionsAnswered: 0,
    exchanges: 0,
    mockSecondsLeft: DEFAULT_CONFIG.mockSeconds,
    mockIndex: 0,
    openBlock: null,
    completedBlocks: [],
    endedReason: null,
  };
}

/**
 * `takeBreak` and `end` carry `goalMet` because deciding it needs the capacity
 * the launcher derived its options from, and the reducer is deliberately
 * capacity-free — storing capacity here would let a mid-session refresh move the
 * goalpost retroactively. Callers compute it with {@link isGoalMet}.
 */
export type FocusSessionEvent =
  | { type: "open"; scope: SessionScope; mode?: SessionMode }
  | { type: "setMode"; mode: SessionMode }
  | { type: "setGoalType"; goalType: GoalType }
  | { type: "setGoalIndex"; index: number }
  | { type: "setGoalMarks"; marks: number }
  | { type: "setScopeTopic"; topicId: string; topicName: string }
  | { type: "confirm" }
  | { type: "booted"; atMs: number }
  | { type: "tick"; atMs: number }
  | { type: "activity" }
  | { type: "answered"; marks: number }
  | { type: "exchange" }
  | { type: "dismissNudge" }
  | { type: "declineBreak" }
  | { type: "takeBreak"; atMs: number; goalMet: boolean }
  | { type: "skipBreak"; atMs: number }
  | { type: "breakElapsed"; atMs: number }
  | { type: "end"; atMs: number; goalMet: boolean }
  | { type: "submitMock" }
  | { type: "close"; atMs: number };

function openBlockAt(blockIndex: number, atMs: number): OpenBlock {
  return {
    blockIndex,
    startedAtMs: atMs,
    activeSeconds: 0,
    idleSeconds: 0,
    nudgeShown: 0,
    nudgeDismissed: 0,
    breakOffered: false,
    breakTaken: false,
    breakSkipped: false,
  };
}

function closeBlock(
  block: OpenBlock,
  atMs: number,
  reason: EndedReason,
  goalMet: boolean,
): FocusBlockRecord {
  return {
    blockIndex: block.blockIndex,
    startedAtMs: block.startedAtMs,
    endedAtMs: atMs,
    activeSeconds: block.activeSeconds,
    idleSecondsTotal: block.idleSeconds,
    endedReason: reason,
    goalMet,
    nudgeShown: block.nudgeShown,
    nudgeDismissed: block.nudgeDismissed,
    breakOffered: block.breakOffered,
    breakTaken: block.breakTaken,
    breakSkipped: block.breakSkipped,
  };
}

/**
 * Whether the goal is met, given the capacity the launcher derived its options
 * from. Capacity is passed in rather than stored so a mid-session capacity
 * refresh cannot retroactively move the goalpost.
 */
export function isGoalMet(
  state: FocusSessionState,
  capacityMinutes: number | null,
): boolean {
  if (state.goalType === "marks") return state.marksEarned >= state.goalMarks;
  return state.activeSeconds >= goalSeconds(state, capacityMinutes);
}

export function goalMinutes(
  state: FocusSessionState,
  capacityMinutes: number | null,
): number {
  const options = timeOptions(capacityMinutes);
  const chosen = options[state.goalIndex] ?? options[options.length - 1];
  return chosen.minutes;
}

export function goalSeconds(
  state: FocusSessionState,
  capacityMinutes: number | null,
): number {
  return goalMinutes(state, capacityMinutes) * 60;
}

/** 0–1 progress toward the goal, for the focus bar's green fill. */
export function goalFraction(
  state: FocusSessionState,
  capacityMinutes: number | null,
): number {
  if (state.goalType === "marks") {
    if (state.goalMarks <= 0) return 0;
    return Math.min(1, state.marksEarned / state.goalMarks);
  }
  const target = goalSeconds(state, capacityMinutes);
  if (target <= 0) return 0;
  return Math.min(1, state.activeSeconds / target);
}

/** True when the stretch option is selected — recorded per block for beta. */
export function isStretchSelected(
  state: FocusSessionState,
  capacityMinutes: number | null,
): boolean {
  if (state.goalType !== "time") return false;
  return timeOptions(capacityMinutes)[state.goalIndex]?.stretch ?? false;
}

/**
 * `mm:ss`. Lives here rather than in a component so the focus bar, the break
 * ring and the mock timer cannot drift apart.
 */
export function formatClock(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const mins = Math.floor(safe / 60);
  const secs = safe % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

export function reduce(
  state: FocusSessionState,
  event: FocusSessionEvent,
  config: FocusSessionConfig = DEFAULT_CONFIG,
): FocusSessionState {
  switch (event.type) {
    case "open":
      // A fresh launcher always starts from clean state: reopening after a
      // completed session must not inherit the previous block's counters.
      return {
        ...initialState(),
        phase: "planning",
        scope: event.scope,
        mode: event.mode ?? "question",
        breakSecondsLeft: config.breakSeconds,
        mockSecondsLeft: config.mockSeconds,
      };

    case "setMode":
      if (state.phase !== "planning") return state;
      return { ...state, mode: event.mode };

    case "setGoalType":
      if (state.phase !== "planning") return state;
      return { ...state, goalType: event.goalType };

    case "setGoalIndex":
      if (state.phase !== "planning") return state;
      return { ...state, goalIndex: Math.max(0, Math.trunc(event.index)) };

    case "setGoalMarks":
      if (state.phase !== "planning") return state;
      return { ...state, goalMarks: Math.max(1, Math.trunc(event.marks)) };

    case "setScopeTopic":
      // A subject-scoped session has no topic until one is resolved (from the
      // carry-on suggestion or the topic row that launched it). Allowed during
      // booting too, because resolution can be part of the boot request.
      if (!state.scope) return state;
      if (state.phase !== "planning" && state.phase !== "booting") return state;
      return {
        ...state,
        scope: {
          ...state.scope,
          topicId: event.topicId,
          topicName: event.topicName,
        },
      };

    case "confirm":
      if (state.phase !== "planning") return state;
      return { ...state, phase: "booting" };

    case "booted": {
      if (state.phase !== "booting") return state;
      // Mock opens NO focus block: it runs under exam conditions and is not
      // focus training (contract D1 restricts mode to practice|tutor).
      if (state.mode === "mock") {
        return {
          ...state,
          phase: "mockRunning",
          mockSecondsLeft: config.mockSeconds,
        };
      }
      return {
        ...state,
        phase: "focusing",
        openBlock: openBlockAt(0, event.atMs),
      };
    }

    case "tick": {
      if (state.phase === "mockRunning") {
        const left = state.mockSecondsLeft - 1;
        if (left <= 0)
          return { ...state, mockSecondsLeft: 0, phase: "mockResults" };
        return { ...state, mockSecondsLeft: left };
      }

      if (state.phase === "break") {
        const left = state.breakSecondsLeft - 1;
        if (left <= 0) {
          // Reset rather than leaving it at 0: a student may take a second
          // break later in the session, and a ring starting at 00:00 would
          // read as already spent.
          return {
            ...state,
            breakSecondsLeft: config.breakSeconds,
            phase: "focusing",
            openBlock:
              state.openBlock ??
              openBlockAt(state.completedBlocks.length, event.atMs),
          };
        }
        return { ...state, breakSecondsLeft: left };
      }

      if (state.phase !== "focusing" || !state.openBlock) return state;

      const sinceActivity = state.secondsSinceActivity + 1;
      // The idle grace: a second only stops counting as work once inactivity
      // has passed the nudge threshold. Without a grace, ordinary reading and
      // thinking time would be scored as idle; without a cap, a tab left open
      // overnight would inflate `active_seconds` and with it the capacity
      // metric that the whole growth story rests on.
      const isIdle = sinceActivity >= config.idleNudgeSeconds;
      const block: OpenBlock = {
        ...state.openBlock,
        activeSeconds: state.openBlock.activeSeconds + (isIdle ? 0 : 1),
        idleSeconds: state.openBlock.idleSeconds + (isIdle ? 1 : 0),
      };

      let nudgeVisible = state.nudgeVisible;
      if (isIdle && !state.nudgeVisible) {
        nudgeVisible = true;
        block.nudgeShown += 1;
      }

      const elapsedSeconds = state.elapsedSeconds + 1;
      let breakOffered = state.breakOffered;
      if (
        !breakOffered &&
        !state.breakDeclined &&
        elapsedSeconds >= config.blockBoundarySeconds
      ) {
        breakOffered = true;
        block.breakOffered = true;
      }

      return {
        ...state,
        elapsedSeconds,
        activeSeconds: state.activeSeconds + (isIdle ? 0 : 1),
        idleSeconds: state.idleSeconds + (isIdle ? 1 : 0),
        secondsSinceActivity: sinceActivity,
        nudgeVisible,
        breakOffered,
        openBlock: block,
      };
    }

    case "activity": {
      // Any sign of life clears the idle counter and retires the nudge. Not
      // recorded as a dismissal: the student got on with it rather than
      // acknowledging a toast.
      if (state.secondsSinceActivity === 0 && !state.nudgeVisible) return state;
      return { ...state, secondsSinceActivity: 0, nudgeVisible: false };
    }

    case "dismissNudge": {
      if (!state.nudgeVisible || !state.openBlock) return state;
      return {
        ...state,
        nudgeVisible: false,
        secondsSinceActivity: 0,
        openBlock: {
          ...state.openBlock,
          nudgeDismissed: state.openBlock.nudgeDismissed + 1,
        },
      };
    }

    case "answered":
      if (state.phase !== "focusing") return state;
      return {
        ...state,
        marksEarned: state.marksEarned + Math.max(0, event.marks),
        questionsAnswered: state.questionsAnswered + 1,
        secondsSinceActivity: 0,
        nudgeVisible: false,
      };

    case "exchange":
      if (state.phase !== "focusing") return state;
      return {
        ...state,
        exchanges: state.exchanges + 1,
        secondsSinceActivity: 0,
        nudgeVisible: false,
      };

    case "declineBreak":
      // "Keep going" — the offer is not made again this session. Breaks are
      // never forced, so declining is a first-class outcome, not a deferral.
      return { ...state, breakOffered: false, breakDeclined: true };

    case "takeBreak": {
      if (state.phase !== "focusing" || !state.openBlock) return state;
      const block = { ...state.openBlock, breakTaken: true };
      return {
        ...state,
        phase: "break",
        breakSecondsLeft: config.breakSeconds,
        breakOffered: false,
        nudgeVisible: false,
        secondsSinceActivity: 0,
        openBlock: null,
        completedBlocks: [
          ...state.completedBlocks,
          closeBlock(block, event.atMs, "break_taken", event.goalMet),
        ],
      };
    }

    case "skipBreak": {
      if (state.phase !== "break") return state;
      // Cutting a break short resumes the next block immediately; the block
      // that ended when the break started is already recorded.
      const blocks = state.completedBlocks.slice();
      const last = blocks[blocks.length - 1];
      if (last) blocks[blocks.length - 1] = { ...last, breakSkipped: true };
      return {
        ...state,
        phase: "focusing",
        breakSecondsLeft: config.breakSeconds,
        completedBlocks: blocks,
        openBlock: openBlockAt(blocks.length, event.atMs),
      };
    }

    case "breakElapsed":
      if (state.phase !== "break") return state;
      return {
        ...state,
        phase: "focusing",
        breakSecondsLeft: config.breakSeconds,
        openBlock: openBlockAt(state.completedBlocks.length, event.atMs),
      };

    case "submitMock":
      if (state.phase !== "mockRunning") return state;
      return { ...state, phase: "mockResults" };

    case "end": {
      // Deliberate end. `goal_met` when the student got what they came for,
      // otherwise `ended_early` — never `abandoned`, which is reserved for
      // walking away and is the only reason excluded from capacity.
      if (state.phase !== "focusing" && state.phase !== "break") return state;
      const reason: EndedReason = event.goalMet ? "goal_met" : "ended_early";
      const blocks = state.openBlock
        ? [
            ...state.completedBlocks,
            closeBlock(state.openBlock, event.atMs, reason, event.goalMet),
          ]
        : state.completedBlocks;
      return {
        ...state,
        phase: "complete",
        openBlock: null,
        completedBlocks: blocks,
        endedReason: reason,
      };
    }

    case "close": {
      // Closing mid-block is abandonment: the block is still recorded, because
      // the instrumentation needs to know how often this happens, but it is
      // marked so it cannot deflate capacity.
      if (state.openBlock) {
        return {
          ...initialState(),
          completedBlocks: [
            ...state.completedBlocks,
            closeBlock(state.openBlock, event.atMs, "abandoned", false),
          ],
          endedReason: "abandoned",
        };
      }
      return {
        ...initialState(),
        completedBlocks: state.completedBlocks,
        endedReason: state.endedReason,
      };
    }
  }
}
